import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveFeatureForPath } from '../config/planFeatures';
import { useUserPlan } from './useUserPlan';

/**
 * Navigate to a path, or block with upgrade flow when plan is insufficient.
 * Route-level blocks are also enforced by PlanPathGuard.
 */
export function usePlanGate() {
  const navigate = useNavigate();
  const { planReady, canUseFeature } = useUserPlan();

  const requireFeature = useCallback(
    (feature) => {
      if (!feature) return true;
      if (!planReady) return false;
      return canUseFeature(feature);
    },
    [planReady, canUseFeature]
  );

  const goToBillingUpgrade = useCallback(
    (feature) => {
      navigate('/settings?tab=billing', { state: { blockedFeature: feature } });
    },
    [navigate]
  );

  /** @returns {boolean} true if navigation proceeded (or will show route guard) */
  const navigateIfAllowed = useCallback(
    (path) => {
      if (!path) return false;
      const feature = resolveFeatureForPath(path);
      if (feature && planReady && !canUseFeature(feature)) {
        navigate(path);
        return false;
      }
      navigate(path);
      return true;
    },
    [navigate, planReady, canUseFeature]
  );

  /** Block an in-page action (not a route). Returns true if blocked. */
  const blockUnlessFeature = useCallback(
    (feature) => {
      if (!feature || !planReady) return false;
      if (canUseFeature(feature)) return false;
      goToBillingUpgrade(feature);
      return true;
    },
    [planReady, canUseFeature, goToBillingUpgrade]
  );

  return {
    planReady,
    canUseFeature,
    requireFeature,
    navigateIfAllowed,
    blockUnlessFeature,
    goToBillingUpgrade,
  };
}

export default usePlanGate;
